<template>
    <PublicPageLayout heading="§public.set_new_password_heading">
        <CoreStringInput
            v-model="inputs.email"
            :validator="v$.email"
            :fullWidth="true"
            placeholder="§ui.placeholder_login_email"
            class="mb-10"
        />

        <CorePasswordInput
            v-model="inputs.password"
            :validator="v$.password"
            :fullWidth="true"
            class="mb-10"
        />

        <CorePasswordInput
            v-model="inputs.passwordConfirm"
            :validator="v$.passwordConfirm"
            :fullWidth="true"
            placeholder="§ui.placeholder_confirm_password"
            class="mb-10"
        />

        <CoreButton
            @click="handleSubmit"
            label="§ui_button.save"
            icon="pi-save"
            :loading="waiting"
            :disabled="!isValid"
        />
    </PublicPageLayout>
</template>

<script>
import { useVuelidate } from '@vuelidate/core'
import { required, minLength, maxLength, email } from '@vuelidate/validators'
import PublicPageLayout from './components/PublicPageLayout.vue'
import CoreButton from '@/components/core/CoreButton.vue'
import CoreStringInput from '@/components/core/CoreStringInput.vue'
import CorePasswordInput from '@/components/core/CorePasswordInput.vue'
import { useAuthStore } from '@/stores/auth'

const noSpaces = (value) => !/\s/.test(value)
const isSame = (inputs) => (value) => inputs.password === value

export default {
    name: 'SetNewPassword',

    components: {
        PublicPageLayout,
        CoreButton,
        CoreStringInput,
        CorePasswordInput,
    },

    props: {
        token: { type: String, default: () => '' },
    },

    data() {
        return {
            inputs: {
                email: '',
                password: '',
                passwordConfirm: '',
            },
            waiting: false,
            v$: {},
        }
    },

    computed: {
        isValid() {
            return this.v$ ? !this.v$.$invalid : false
        },

        rules() {
            return {
                email: { required, minLength: minLength(2), maxLength: maxLength(100), email },
                password: {
                    required,
                    minLength: minLength(6),
                    maxLength: maxLength(100),
                    noSpaces,
                },
                passwordConfirm: {
                    required,
                    minLength: minLength(6),
                    maxLength: maxLength(100),
                    sameAsPassword: isSame(this.inputs),
                },
            }
        },
    },

    created() {
        this.authStore = useAuthStore()
    },

    mounted() {
        this.v$ = useVuelidate(this.rules, this.inputs)
        this.prefillEmail()
    },

    watch: {
        'inputs.email'() {
            if (this.v$ && this.v$.email) {
                this.v$.email.$touch()
            }
        },
    },

    methods: {
        async handleSubmit() {
            this.v$.$touch()
            if (this.waiting || this.v$.$invalid) return

            this.waiting = true
            try {
                await this.authStore.setNewPassword({
                    email: this.inputs.email,
                    token: this.token || this.$attrs.token,
                    password: this.inputs.password,
                    passwordConfirm: this.inputs.passwordConfirm,
                })
                this.$router.push({ name: 'login' })
            } catch (error) {
                this.$toast.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: error.message || 'An unexpected error occurred',
                    life: 2000,
                })
            } finally {
                this.waiting = false
            }
        },

        prefillEmail() {
            const queryString = window.location.search.substring(1)
            const pairs = queryString.split('&')
            let emailParam = ''

            for (const pair of pairs) {
                const [key, value] = pair.split('=')
                if (key === 'email' && value) {
                    emailParam = decodeURIComponent(value.replace(/\+/g, '%2B'))
                    break
                }
            }

            this.inputs.email = emailParam || ''
        },
    },
}
</script>
